import React, { useState, useEffect, useRef, useMemo } from "react";
import { PropTypes } from "prop-types";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import TableStore from "@harbor/elements/utils/table/store";
import useColumns from "@harbor/elements/utils/table/hooks/columns";
import useRowSizes from "@harbor/elements/utils/table/hooks/rowSizes";

import OverLappingSpinner from "../../../common/Spinner/OverLappingSpinner";
import ErrorComponent from "../../../common/ErrorComponent";
import { useMount } from "../../../utils/genericCommon";
import {
  setCircuitsTableData,
  getReportTableColumns,
  getReportTableColumnsLinkUtilization,
  getReportTableLinkUtilizationBandwidth,
  getReportTableLinkAvailabitlity,
} from "./tableConfig";
import apiService from "../../../config/api-config";
import { useI18n } from "../../../i18n/useI18n";
import { REPORT_NAMES } from "../../../utils/enums";

const [HbrCard, HbrIcon, HbrTable] = reactWrapper([
  "hbr-card",
  "hbr-icon",
  "hbr-table",
]);

const defaultSort = [["total_bps", "desc"]];
const tableId = "circuit_dashboard_table_main";

const getColumns = (reportName) => {
  if (reportName === REPORT_NAMES.REPORT_LINK_UTILIZATION) {
    return getReportTableColumnsLinkUtilization();
  }
  if (reportName === REPORT_NAMES.REPORT_LINK_UTILIZATION_BANDWIDTH) {
    return getReportTableLinkUtilizationBandwidth();
  }
  if (reportName === REPORT_NAMES.REPORT_LINK_AVAILABILITY) {
    return getReportTableLinkAvailabitlity();
  }
  return getReportTableColumns();
};

const Table = ({
  globalV4PayloadReport,
  reportName,
  isreportCallback = () => null,
  selectedReportData = () => null,
}) => {
  const { tViews } = useI18n("views");
  const [loader, setLoader] = useState(true);
  const [isLoadingComplete, setIsLoadingComplete] = useState(false);
  const [, forceUpdate] = useState(null);
  const mount = useMount();
  const dataRef = useRef({ timestamp: 0, error: null });

  const tableStore = useMemo(() => {
    const columnsHook = useColumns({
      config: () => getColumns(reportName),
      order: defaultSort,
    });
    const rowSizesHook = useRowSizes({ contentHeight: 20 });

    return new TableStore({
      query: { page: 0, pageLength: 100 },
      hooks: [columnsHook, rowSizesHook],
      fetchData: async ({ query, setSource, setCount }) => {
        const timestamp = Date.now();
        const sortMap = {
          [REPORT_NAMES.REPORT_LINK_SLA]: { loss: "desc" },
          [REPORT_NAMES.REPORT_LINK_UTILIZATION]: { total_bps: "desc" },
        };
        const payload = {
          ...globalV4PayloadReport,
          size: query.pageLength,
          offset: query.page * query.pageLength,
          sort: sortMap[reportName] || { availability: "asc" },
        };
        Object.assign(dataRef.current, { timestamp, error: null });
        setLoader(true);

        const res = await apiService.getCircuitsTableList(payload);
        if (mount.mounted && timestamp === dataRef.current.timestamp) {
          let tableData = [];
          let totalCount = 0;
          if (res.errorObject instanceof Object) {
            dataRef.current.error = res.errorObject;
          } else if (
            res.data instanceof Object &&
            Array.isArray(res.data.data)
          ) {
            tableData = setCircuitsTableData(res.data.data);
            if (Number.isInteger(res.data.count)) totalCount = res.data.count;
          }

          setSource(tableData);
          setCount(totalCount, false);
          setLoader(false);
          setIsLoadingComplete(true);
          selectedReportData(tableData);
          isreportCallback(true);
        }
      },
    });
  }, [globalV4PayloadReport, reportName]);

  useEffect(() => {
    const unsubscribe = tableStore.subscribe(forceUpdate);
    return () => unsubscribe();
  }, [tableStore]);

  useEffect(() => {
    setIsLoadingComplete(!loader);
  }, [loader]);

  const { source, columns, count } = tableStore.getState();

  return (
    <HbrCard
      className="widget-container report-circuit-table"
      data-testid="circuit_circuits_table"
      container
    >
      <div className="table-container hbr-css__layout-col-md">
        {loader && <OverLappingSpinner />}
        <div
          aria-live="assertive"
          style={{
            position: "absolute",
            width: "1px",
            height: "1px",
            overflow: "hidden",
          }}
        >
          {isLoadingComplete ? tViews("a11yLabels.loadingCompleted") : ""}
        </div>
        <div className="hbr-css__layout-col-md">
          <div
            className="hbr-css__text-heading-lg-bold flex-items"
            role="heading"
            aria-level="2"
          >
            {tViews("circuit.reportTitle")}
          </div>
        </div>
        <HbrTable
          id={tableId}
          data-testid={tableId}
          columns={columns}
          headerSlot={true}
          readonly
          resize
          style={{ minHeight: "300px", height: `${source.length * 37.5}px` }}
          frameSize={source.length}
          serverSide
          externalSorting
          canFocus={false}
          useClipboard={false}
          onHbr-before-sort={(event) => {
            const { column, order } = event.detail;
            const columnsHook = tableStore.getHook("columns");
            if (order) columnsHook.sort({ order: [[column.id, order]] });
            else if (column.id === defaultSort[0][0]) {
              columnsHook.sort({ order: [[column.id, "asc"]] });
            } else {
              columnsHook.sort({ order: defaultSort });
            }
          }}
          source={source}
        >
          {count === 0 && !loader && (
            <div slot="empty" className="hbr-css__table-empty-state">
              {dataRef.current.error === null ? (
                <>
                  <HbrIcon size="75px" sentiment="neutral" name="empty-state" />
                  <span className="hbr-css__text-secondary-sm">
                    {tViews("c360.tEmpty")}
                  </span>
                </>
              ) : (
                <ErrorComponent
                  {...dataRef.current.error}
                  className="small-dashlet-error"
                  width="110px"
                />
              )}
            </div>
          )}
        </HbrTable>
      </div>
    </HbrCard>
  );
};

Table.propTypes = {
  globalV4PayloadReport: PropTypes.object.isRequired,
  reportName: PropTypes.string,
  isreportCallback: PropTypes.func,
  selectedReportData: PropTypes.func,
};

export default Table;
